import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  Stack,
  Chip,
  Link,
} from "@mui/material";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import React from "react";
import {
  FiltersChipsBills,
  InfoFiltersSelectedData,
} from "../../core/models/bills/bills.model";
import "dayjs/locale/es";
import "./DrawerFilters.scss";
import theme from "../../muiTheme";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../utils/formatters/formatters.util";

export interface DeleteAllFiltersButtonProps {
  totalRegistersQuery: number;
  totalFiltersApplied: number;
  handleDeleteAll: () => void;
  isTableLoading: boolean;
  showNoResults: boolean;
}

export const DeleteAllFiltersButton = ({
  totalRegistersQuery,
  totalFiltersApplied,
  handleDeleteAll,
  isTableLoading,
  showNoResults,
}: DeleteAllFiltersButtonProps) => {
  const { t } = useTranslation();
  const showDeleteAllFiltersButton = !!totalFiltersApplied;
  const infoFiltersSelectedData: InfoFiltersSelectedData[] = [
    {
      id: "results",
      condition: !isTableLoading,
      value: isTableLoading || showNoResults ? 0 : totalRegistersQuery,
      label: totalRegistersQuery === 1 ? "Resultado" : "Resultados",
    },
    {
      id: "appliedFilters",
      condition: !!totalFiltersApplied,
      value: totalFiltersApplied,
      label:
        totalFiltersApplied === 1 ? "Filtro aplicado" : "Filtros aplicados",
    },
  ];

  return (
    <Box component="div" className="filters-delete-all-container">
      <Box component="div" display={"flex"} gap={"1rem"} flexWrap={"wrap"}>
        {infoFiltersSelectedData.map(
          ({ id, condition, value, label }, indexNumber) =>
            condition && (
              <React.Fragment key={id}>
                {!!indexNumber && !isTableLoading && <span>|</span>}
                <Typography variant="subtitle2" component="p">{`${formatNumber(
                  value
                )} ${t(`Facturas Página.${label}`)}`}</Typography>
              </React.Fragment>
            )
        )}
        {showDeleteAllFiltersButton && (
          <Link
            component="button"
            onClick={() => {
              handleDeleteAll();
            }}
          >
            {t("Tablas.Borrar todo")}
          </Link>
        )}
      </Box>
    </Box>
  );
};

interface DrawerFiltersButtonProps {
  setIsFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled: boolean;
  totalFiltersChips: number;
}

export const DrawerFiltersButton = ({
  setIsFiltersOpen,
  isDisabled,
  totalFiltersChips,
}: DrawerFiltersButtonProps) => {
  const { t } = useTranslation();

  return (
    <ButtonElement
      onClick={() => setIsFiltersOpen(true)}
      secondary
      iconL
      iconName="t-icon-filter"
      disabled={isDisabled}
    >
      {t("Tablas.Filtros")}{" "}
      {totalFiltersChips && (
        <span className="filters__filter-count">{totalFiltersChips}</span>
      )}
    </ButtonElement>
  );
};

interface DrawerFilterChipsProps {
  filterChips: FiltersChipsBills[];
  onDelete: (filterHistoricKey: string) => void;
}

export const DrawerFiltersChips = ({
  filterChips,
  onDelete,
}: DrawerFilterChipsProps) => {
  return (
    <>
      {filterChips.map(({ filterBillsKey, label, totalValues }) => (
        <Chip
          className="MuiChip__filter-chip"
          key={filterBillsKey}
          label={
            <Typography>
              {label}{" "}
              {totalValues > 1 && (
                <span className="filters__filter-count">
                  +{totalValues - 1}
                </span>
              )}
            </Typography>
          }
          onDelete={() => onDelete(filterBillsKey)}
        />
      ))}
    </>
  );
};

export interface DrawerFiltersProps {
  isFiltersOpen: boolean;
  onApplyFilters: () => void;
  onClose: () => void;
  onClearFilters: () => void;
  children: JSX.Element;
}

const DrawerFiltersContainer = ({
  isFiltersOpen,
  onApplyFilters,
  onClose,
  onClearFilters,
  children,
}: DrawerFiltersProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useMediaQuery("(max-width: 500px )");
  //   id: ServiceEnvirormentDataId,
  //   value: string,
  //   checked: boolean
  // ) => {
  //   if (!value) return;

  //   setDrawerFilters((prevDrawerFiltersBills) => {
  //     if (id === "environmentCode") {
  //       return {
  //         ...prevDrawerFiltersBills,
  //         [id]: checked ? value : "",
  //       };
  //     }

  //     let currentValues: string[] = [];

  //     const prevValue = prevDrawerFiltersBills[id] as unknown;

  //     if (Array.isArray(prevValue)) {
  //       currentValues = prevValue as string[];
  //     } else if (typeof prevValue === "string") {
  //       currentValues = [prevValue];
  //     }

  //     if (checked) {
  //       return {
  //         ...prevDrawerFiltersBills,
  //         [id]: [...currentValues, value].filter(Boolean),
  //       };
  //     } else {
  //       return {
  //         ...prevDrawerFiltersBills,
  //         [id]: currentValues.filter((item: string) => item !== value),
  //       };
  //     }
  //   });
  // };

  return (
    <Drawer
      open={isFiltersOpen}
      PaperProps={
        isMobileViewport
          ? { sx: { width: "100%" } }
          : { sx: { width: "31rem" } }
      }
    >
      <Box
        component="div"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={{ xs: "1.5rem 1rem", md: "1.5rem 2.25rem" }}
        className="filters-filter-box"
      >
        <Typography component="h3" variant="h3">
          {t("Tablas.Filtros")}
        </Typography>
        <IconButton disableRipple onClick={onClose}>
          <span className="t-icon-close brand-red" />
        </IconButton>
      </Box>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"2rem"}
        sx={{
          padding: { md: "2rem 2.25rem 0 2.25rem", xs: "2rem 1rem 0 1rem" },
        }}
      >
        {children}
      </Box>
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        component="div"
        justifyContent="space-between"
        alignItems="center"
        padding={isMobileViewport ? "0.75rem 1rem" : "0.75rem 2.5rem"}
        position="fixed"
        width={isMobileViewport ? "100%" : "31rem"}
        boxSizing="border-box"
        bottom="0"
        gap={"1rem"}
        className="bottom-filters"
        bgcolor={theme.palette.background.paper}
      >
        <ButtonElement
          onClick={onClearFilters}
          styles={{ flexGrow: isMobileViewport ? "1" : "auto" }}
          link
        >
          {t("Tablas.Borrar filtros")}
        </ButtonElement>
        <ButtonElement onClick={onApplyFilters}>
          {t("Tablas.Aplicar filtros")}
        </ButtonElement>
      </Stack>
    </Drawer>
  );
};

export default DrawerFiltersContainer;
